import { Link, useLocation } from 'react-router-dom';
import type { ComponentType, SVGProps } from 'react';
import { ArrowLeft, CreditCard, Group, User } from 'iconoir-react';
// Figma's Usage icon (10597:101) is lucide's Gauge; iconoir's DashboardSpeed adds tick dots.
import { Gauge } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useSidebar } from 'components/ui/sidebar';
import { isPathActive } from 'utils/is-path-active';
import { useCommonStore } from 'state/store/common/useCommonStore';
import {
  sidebarNavRowHeightClassName,
  sidebarSidePanelClassName,
} from './sidebar-nav-item-styles';

export const SETTINGS_ACCOUNT_PATH = '/account';
// /integrations already redirects to the Knowledge Base tab, so the new page lives under /settings.
export const SETTINGS_INTEGRATIONS_PATH = '/settings/integrations';

type SettingsNavItem = {
  id: string;
  labelKey: string;
  defaultLabel: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Items without a path are not built yet and render disabled. */
  path?: string;
  /** Shown only to admins and org admins. */
  isAdminOnly?: boolean;
  /** Figma 10631:5785 — pending Teams requests count. */
  badge?: number;
};

const SETTINGS_NAV_ITEMS: SettingsNavItem[] = [
  {
    id: 'account',
    labelKey: 'ACCOUNT',
    defaultLabel: 'Account',
    icon: User,
    path: SETTINGS_ACCOUNT_PATH,
  },
  { id: 'usage', labelKey: 'USAGE', defaultLabel: 'Usage', icon: Gauge, path: '/usage' },
  {
    id: 'teams',
    labelKey: 'TEAMS',
    defaultLabel: 'Teams',
    icon: Group,
    path: '/identity-management',
    isAdminOnly: true,
    // Requests badge is hidden until the Requests tab ships (see notification_flag in teams-page).
  },
  // Hidden for now; the page is still reachable at SETTINGS_INTEGRATIONS_PATH.
  // Restore this item and the ViewGrid import to show it again.
  // {
  //   id: 'integrations',
  //   labelKey: 'SETTINGS_INTEGRATIONS',
  //   defaultLabel: 'Integrations',
  //   icon: ViewGrid,
  //   path: SETTINGS_INTEGRATIONS_PATH,
  // },
  {
    id: 'plans-billing',
    labelKey: 'PLANS_AND_BILLING',
    defaultLabel: 'Plans & Billing',
    icon: CreditCard,
    path: '/plans-billing',
    isAdminOnly: true,
  },
];

export const isSettingsRoute = (pathname: string) =>
  SETTINGS_NAV_ITEMS.some((item) => item.path && isPathActive(pathname, item.path));

// INTEGRATIONS is already taken by an "Up to {{value}} integrations" string, so Integrations has its own key.
// Figma "Settings list" (10596:299): 223×38 rows, radius 10, 18px icons at a 1.2px stroke.
// iconoir draws on a 24px grid, so a 1.6 stroke at 18px renders as 1.2px.
const ICON_SIZE = 18;
const ICON_STROKE = 1.6;
const rowClassName = cn(
  sidebarNavRowHeightClassName,
  'flex w-full items-center gap-3 rounded-card px-2.5 text-left text-sm leading-body transition-colors'
);

interface SidebarSettingsPanelProps {
  onBack: () => void;
}

export function SidebarSettingsPanel({ onBack }: SidebarSettingsPanelProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isSidebarOverlay, setOpenMobile } = useSidebar();
  const isAdmin = useCommonStore((state) => state.isAdmin || state.isOrgAdmin);
  const navItems = SETTINGS_NAV_ITEMS.filter((item) => isAdmin || !item.isAdminOnly);

  return (
    <aside
      data-testid="sidebar-settings-panel"
      aria-label={t('SETTINGS', { defaultValue: 'Settings' })}
      className={cn(
        'shrink-0',
        // Same box and top padding as the chats panel so only the contents change.
        isSidebarOverlay ? 'flex w-full flex-col' : cn(sidebarSidePanelClassName, 'px-3 pb-6 pt-6')
      )}
    >
      <div className="flex flex-col">
        <button
          type="button"
          onClick={onBack}
          className={cn(
            rowClassName,
            'text-grey-600 hover:bg-grey-100 dark:text-grey-300 dark:hover:bg-sidebar-pill-dark'
          )}
        >
          <ArrowLeft
            width={ICON_SIZE}
            height={ICON_SIZE}
            strokeWidth={ICON_STROKE}
            className="shrink-0"
          />
          <span>{t('BACK', { defaultValue: 'Back' })}</span>
        </button>

        <h2 className="mb-2 mt-4 px-2.5 text-lg font-medium leading-normal text-grey-950 dark:text-true-white">
          {t('SETTINGS', { defaultValue: 'Settings' })}
        </h2>

        <nav className="flex flex-col gap-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const label = t(item.labelKey, { defaultValue: item.defaultLabel });
            const isActive = Boolean(item.path && isPathActive(pathname, item.path));
            const content = (
              <>
                <Icon
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  strokeWidth={ICON_STROKE}
                  className="shrink-0"
                />
                <span className="min-w-0 flex-1 truncate">{label}</span>
                {item.badge != null && (
                  <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-grey-900 text-xs font-medium leading-heading text-true-white">
                    {item.badge}
                  </span>
                )}
              </>
            );

            if (!item.path) {
              return (
                <button
                  key={item.id}
                  type="button"
                  disabled
                  className={cn(
                    rowClassName,
                    'cursor-not-allowed text-grey-600 dark:text-grey-300'
                  )}
                >
                  {content}
                </button>
              );
            }

            return (
              <Link
                key={item.id}
                to={item.path}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => {
                  if (isSidebarOverlay) setOpenMobile(false);
                }}
                className={cn(
                  rowClassName,
                  'hover:bg-grey-100 dark:hover:bg-sidebar-pill-dark',
                  isActive
                    ? 'bg-grey-100 font-medium text-grey-950 dark:bg-sidebar-pill-dark dark:text-grey-50'
                    : 'text-grey-600 dark:text-grey-300'
                )}
              >
                {content}
              </Link>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
